import React, { useEffect } from "react";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router-dom";
import {
  scrollToTop,
  parseCurrencyToNumber,
  safeNumber,
} from "../formHelpers";

const CitizenshipNavigateEffect = ({ allUSUKCanada }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (allUSUKCanada === "No") {
      const timer = setTimeout(() => {
        navigate(-1);
      }, 5000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [allUSUKCanada, navigate]);

  return null;
};

const CITIZENSHIP_LABEL = "Are all Trustees, U.S, U.K or Canada citizens??";
const EXPLANATION_LABEL = "Provide an explanation";
const AUTHORIZATION_FIELD_LABEL =
  "Authorization to obtain underwriting information?";

const meetsAuthorizationThreshold = (globalData, authorizationCfg) => {
  if (!authorizationCfg) {
    return false;
  }

  const bondAmount = globalData?.basicInformation?.bondAmount;

  if (authorizationCfg.useGuardianshipRetrievalThreshold) {
    const threshold =
      globalData?.basicInformation?.bondSubCategory === "Guardian - Minor"
        ? 50000
        : 250000;
    return bondAmount >= threshold;
  }

  if (authorizationCfg.useParseCurrency) {
    return (
      safeNumber(parseCurrencyToNumber(bondAmount)) >=
      authorizationCfg.threshold
    );
  }

  return bondAmount >= authorizationCfg.threshold;
};

const BondGeneralInformationBase = ({
  cfg,
  data = {},
  updateData,
  globalData,
  errors = {},
  isDisabled = false,
}) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateData({ ...data, [name]: value });
  };

  const handleExplanationChange = (e) => {
    const { name, value } = e.target;
    updateData({
      ...data,
      explanations: {
        ...data?.explanations,
        [name]: value || "",
      },
    });
  };

  useEffect(() => {
    if (cfg.scrollToTop) {
      scrollToTop();
    }
  }, [cfg.scrollToTop]);

  const explanations = data?.explanations || cfg.explanationDefaults;
  const showAuthorization = meetsAuthorizationThreshold(
    globalData,
    cfg.authorization
  );

  const renderYesNoQuestion = (question) => {
    const {
      name,
      label,
      defaultValue = "No",
      required = true,
      showExplanationWhenYes = false,
      explanationKey = name,
    } = question;

    const explanationFieldProps = cfg.multilineExplanations
      ? { multiline: true, minRows: 2, maxRows: 6 }
      : {};

    return (
      <React.Fragment key={name}>
        <TextField
          disabled={isDisabled}
          select
          label={label}
          name={name}
          value={data[name] || defaultValue}
          onChange={handleChange}
          fullWidth
          required={required}
          error={cfg.showErrors ? !!errors[name] : false}
          helperText={cfg.showErrors ? errors[name] : undefined}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {showExplanationWhenYes && data[name] === "Yes" && (
          <TextField
            disabled={isDisabled}
            label={EXPLANATION_LABEL}
            name={explanationKey}
            value={explanations[explanationKey] || ""}
            onChange={handleExplanationChange}
            fullWidth
            {...explanationFieldProps}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <Box
      data-testid="bond-wizard-step-general"
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        General Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        {cfg.estimatedAssetsField && (
          <TextField
            disabled={isDisabled}
            label="Estimated Assets"
            name="estAssets"
            value={data.estAssets || ""}
            onChange={handleChange}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
          />
        )}

        {cfg.citizenshipQuestion?.navigateOnNo && (
          <CitizenshipNavigateEffect allUSUKCanada={data.allUSUKCanada} />
        )}

        {cfg.citizenshipQuestion && (
          <>
            <TextField
              disabled={isDisabled}
              select
              label={CITIZENSHIP_LABEL}
              name="allUSUKCanada"
              value={data.allUSUKCanada || cfg.citizenshipQuestion.defaultValue}
              onChange={handleChange}
              fullWidth
              required
              error={cfg.showErrors ? !!errors.allUSUKCanada : false}
              helperText={cfg.showErrors ? errors.allUSUKCanada : undefined}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>

            {cfg.citizenshipQuestion.showWarningOnNo &&
              data.allUSUKCanada === "No" && (
                <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
                  <Alert
                    severity="warning"
                    icon={<WarningAmberIcon fontSize="inherit" />}
                  >
                    <AlertTitle>Warning</AlertTitle>
                    The court order by <strong>Judge Stearn</strong> is a
                    mandatory requirement to create this bond. Please try again
                    once the order is issued.
                  </Alert>
                </Stack>
              )}
          </>
        )}

        {cfg.questions.map(renderYesNoQuestion)}

        {showAuthorization && (
          <>
            <Box
              sx={{
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                backgroundColor: "#f9fafb",
                padding: "16px",
                margin: "16px 0",
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: "2px" }}>
                {cfg.authorizationBanner.title}
              </Typography>
              <Typography>{cfg.authorizationBanner.body}</Typography>
            </Box>

            <TextField
              disabled={isDisabled}
              select
              label={AUTHORIZATION_FIELD_LABEL}
              name="requestForAuthorization"
              value={
                data.requestForAuthorization ||
                cfg.authorization.requestForAuthorizationDefault
              }
              onChange={handleChange}
              fullWidth
              required
            >
              {cfg.authorization.showEmptyAuthorizationOption && (
                <MenuItem value="" disabled>
                  Select an option
                </MenuItem>
              )}
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </>
        )}
      </Box>
    </Box>
  );
};

export default BondGeneralInformationBase;
